/**
 * StateService.ts;
 * Service de gestion d'état centralisé avec Zustand;
 * Remplace la gestion d'état dispersée dans les différents managers;
 *
 * MIGRATION REACT: Ce store est maintenant la SOURCE UNIQUE DE VÉRITÉ
 * pour les shapes du canvas. Les composants React lisent et écrivent
 * directement dans ce store.
 */
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { TextStyle, TemplateFormat, TemplateUnit } from '../types';
import { generateShapeId, type ShapeData } from '../types/ShapeTypes';
import type { TransformPayload } from '../types/TransformTypes';
import type { Viewport, ViewportPosition } from '../types/ViewportTypes';
import { zustandHistoryMiddleware } from './ZustandHistoryMiddleware';
import { sanitizeShapes } from '../utils/ProjectSanitizer';
import { normalizeShapeData } from '../utils/shapeNormalization';
import { EventBus } from './EventBus';
import {
  getSelectionBBox,
  getShapeRect,
  getShapeWHForOps,
  getShapeWorldCenter,
  originFromWorldCenter,
} from '../utils/shapeRect';
import { rotatePointAround, clamp } from '../utils/geometry';
import {
  StateServiceEventType,
  type ShapeAddedEventData,
  type ShapeUpdatedEventData,
  type ShapeDeletedEventData,
  type SelectionChangedEventData,
  type ProjectDimensionsChangedEventData,
  type ProjectFormatChangedEventData,
  type CanvasZoomChangedEventData,
} from './StateServiceEvents';

// ============================================================================
// TYPES POUR LE PROJET (PHASE 1 - Fondation Réactive)
// ============================================================================

/**
 * Format physique du template (dimensions en mm)
 */
export interface PhysicalFormat {
  id: string;
  name: string;
  widthMm: number;
  heightMm: number;
  description?: string;
}

/**
 * Formats prédéfinis pour photobooth
 */
export const PRESET_FORMATS: PhysicalFormat[] = [
  {
    id: 'photobooth_10x15',
    name: 'Photobooth 10×15 cm',
    widthMm: 100,
    heightMm: 150,
    description: 'Format standard photobooth',
  },
  {
    id: 'photobooth_15x10',
    name: 'Photobooth 15×10 cm (paysage)',
    widthMm: 150,
    heightMm: 100,
    description: 'Format photobooth paysage',
  },
  { id: '10x15', name: '10×15 cm', widthMm: 100, heightMm: 150, description: 'Format standard' },
  {
    id: '15x10',
    name: '15×10 cm (paysage)',
    widthMm: 150,
    heightMm: 100,
    description: 'Format paysage',
  },
  { id: '10x10', name: '10×10 cm', widthMm: 100, heightMm: 100, description: 'Format carré' },
  {
    // Carré réseaux sociaux 1080×1080 px (à 300 DPI). Référencé par le dashboard.
    id: 'square_1080',
    name: 'Carré 1080 px',
    widthMm: 91.44,
    heightMm: 91.44,
    description: 'Carré réseaux sociaux (1080×1080 px)',
  },
  {
    // Story / vertical 1080×1920 px (à 300 DPI). Référencé par le dashboard.
    id: 'story_9x16',
    name: 'Story 9:16',
    widthMm: 91.44,
    heightMm: 162.56,
    description: 'Story vertical (1080×1920 px)',
  },
  {
    id: '2x6_strip',
    name: 'Bandelette 2×6"',
    widthMm: 50.8,
    heightMm: 152.4,
    description: 'Photo strip classique',
  },
  {
    id: '4x6',
    name: '4×6" (10×15 cm)',
    widthMm: 101.6,
    heightMm: 152.4,
    description: 'Format US standard',
  },
  {
    id: 'custom',
    name: 'Personnalisé',
    widthMm: 100,
    heightMm: 150,
    description: 'Dimensions personnalisées',
  },
];

/**
 * DPI prédéfinis avec qualité associée
 */
export const PRESET_DPI = {
  WEB: { value: 72, label: 'Web (72 DPI)', quality: 'low' },
  STANDARD: { value: 150, label: 'Standard (150 DPI)', quality: 'medium' },
  PRINT: { value: 300, label: 'Impression (300 DPI)', quality: 'high' },
  PROFESSIONAL: { value: 600, label: 'Professionnel (600 DPI)', quality: 'ultra' },
} as const;

/**
 * Type de booth cible (pour configurations spécifiques)
 */
export type TargetBoothType =
  | 'dnp_ds620'
  | 'dnp_rx1'
  | 'hiti_p520l'
  | 'mitsubishi_cp3800'
  | 'generic'
  | 'custom';

/**
 * Configuration d'impression (marges de sécurité)
 */
export interface PrintSettings {
  bleedMm: number; // Fond perdu en mm (typiquement 2-3mm)
  safeZoneMm: number; // Zone de sécurité en mm (typiquement 5mm)
  cutMarkEnabled: boolean; // Afficher les repères de coupe
}

/**
 * État du projet - Configuration globale du template
 */
export interface ProjectState {
  // Identification
  id: string | null;
  name: string;
  createdAt: Date | null;
  modifiedAt: Date | null;
  projectVersion: string; // Version du format de projet (ex: "1.0.0")

  // Dimensions physiques
  format: PhysicalFormat;
  dpi: number;

  // Dimensions calculées en pixels
  pixelWidth: number;
  pixelHeight: number;

  // Booth cible
  targetBooth: TargetBoothType;

  // Configuration d'impression
  printSettings: PrintSettings;

  // Zones de sécurité visuelles
  safeZoneVisible: boolean;
  bleedVisible: boolean;
}

/**
 * Sélection robuste supportant le multi-select
 */
export interface SelectionState {
  // IDs des éléments sélectionnés
  selectedIds: string[];

  // ID de l'élément principal (dernier cliqué ou premier de la sélection)
  primaryId: string | null;

  // Mode de sélection actuel
  mode: 'single' | 'multi' | 'marquee';

  // Rectangle de sélection (pour marquee selection)
  marqueeRect: { x: number; y: number; width: number; height: number } | null;

  // Historique de sélection (pour Shift+Click)
  lastClickedId: string | null;
}

// Types pour l'état;
export interface SelectedNodeProperties {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  rotation?: number;
  opacity?: number;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  fontSize?: number;
  fontFamily?: string;
  fontStyle?: string;
  fontWeight?: string;
  text?: string;
  textAlign?: string;
  textDecoration?: string;
  scaleX?: number;
  scaleY?: number;
  skewX?: number;
  skewY?: number;
  shadowBlur?: number;
  shadowColor?: string;
  shadowOffsetX?: number;
  shadowOffsetY?: number;
  cornerRadius?: number;
  blendMode?: string;
  [key: string]: unknown;
}

export interface CanvasState {
  // NETTOYÉ: selectedNodeId et selectedNodeIds supprimés - utiliser selection.selectedIds et selection.primaryId
  selectedNodeProperties: SelectedNodeProperties | null;
  hoveredNodeId: string | null; // PHASE 3.1: Pour le feedback hover bidirectionnel
  zoomLevel: number;
  // NETTOYÉ: Viewport centralisé dans Zustand (source unique de vérité)
  viewport: Viewport; // Viewport du canvas (scale + position)
  gridEnabled: boolean;
  gridSize: number;
  snapToGrid: boolean;
  alignmentGuidesEnabled: boolean;
  backgroundColor: string;
  templateWidth: number;
  templateHeight: number;
  isPreviewMode: boolean;
  isExporting: boolean;
  documentName: string;
}
export interface UIState {
  leftSidebarCollapsed: boolean;
  rightSidebarCollapsed: boolean;
  activeTool: string | null;
  showColorPicker: boolean;
  showTemplateGallery: boolean;
  showAssetsLibrary: boolean;
}
/**
 * État pour l'outil texte avancé
 */
export interface TextToolState {
  activeTextStyle: string | null;
  savedStyles: TextStyle[];
  showTextStylesPanel: boolean;
}
/**
 * États de sauvegarde possibles;
 */
// A16 : 'offline' = localStorage sauvegardé mais pas Supabase (réseau absent).
export type SaveStatus = 'idle' | 'unsaved' | 'saving' | 'saved' | 'error' | 'conflict' | 'offline';
/**
 * État de sauvegarde;
 */
export interface SaveState {
  status: SaveStatus;
  lastSaved: Date | null;
  errorMessage?: string;
}
/**
 * PR-08: Type de geste utilisateur
 */
export type GestureType = 'drag' | 'transform' | 'property' | 'unknown';

/**
 * PR-12: Types pour l'alignement et la distribution
 */
export type AlignMode = 'left' | 'centerX' | 'right' | 'top' | 'centerY' | 'bottom';

export type DistributeMode = 'horizontal' | 'vertical';

/**
 * PR-14: Types pour le mirror/flip
 */
export type FlipAxis = 'horizontal' | 'vertical';
export type FlipMode = 'mirror' | 'flip';

/**
 * PR-16: Types pour le grouping
 */
export interface GroupData {
  id: string;
  name: string;
  childIds: string[];
  createdAt: number;
}

/**
 * PR-08: État du geste en cours (pour transaction unique d'historique)
 */
export interface GestureState {
  active: boolean;
  type: GestureType | null;
  ids: string[];
  startedAt: number | null;
}
export interface AppState {
  // MIGRATION REACT: shapes est maintenant la source unique de vérité
  shapes: ShapeData[];
  canvas: CanvasState;
  ui: UIState;
  history: {
    canUndo: boolean;
    canRedo: boolean;
    historyLength: number;
  };
  layers: {
    layerCount: number;
    layerOrder: string[];
  };
  textTool: TextToolState;
  save: SaveState;

  // PHASE 1: Nouveaux états pour la fondation réactive
  project: ProjectState;
  selection: SelectionState;
  // PR-08: État du geste pour transaction unique d'historique
  gesture: GestureState;
  // PR-16: Groupes logiques
  groups: GroupData[];
}
// Actions pour modifier l'état;
export interface AppActions {
  // Shape actions (MIGRATION REACT: Source unique de vérité);
  addShape: (shape: ShapeData) => void;
  updateShape: (id: string, updates: Partial<ShapeData>) => void;
  updateShapes: (updates: Array<{ id: string; updates: Partial<ShapeData> }>) => void; // PHASE 3: Batch updates pour éviter le spam d'historique
  applyTransform: (id: string, transform: TransformPayload) => void; // Action pour appliquer une transformation (transform/drag)
  deleteShape: (id: string) => void;
  duplicateShape: (id: string) => void;
  // PR-09: Actions multi-select
  deleteShapes: (ids: string[]) => void;
  duplicateShapes: (ids: string[]) => void;
  loadShapes: (shapes: ShapeData[]) => void;
  clearShapes: () => void;
  moveShapeUp: (id: string) => void;
  moveShapeDown: (id: string) => void;
  moveShapeToTop: (id: string) => void;
  moveShapeToBottom: (id: string) => void;
  // Alias pour cohérence avec le plan
  bringToFront: (id: string) => void;
  sendToBack: (id: string) => void;
  forward: (id: string) => void;
  backward: (id: string) => void;
  // Group actions;
  groupShapes: (shapeIds: string[]) => void;
  ungroupShape: (groupId: string) => void;
  // Canvas actions;
  setSelectedNode: (nodeId: string | null) => void;
  setSelectedNodes: (nodeIds: string[]) => void;
  setHoveredNode: (nodeId: string | null) => void; // PHASE 3.1: Pour le feedback hover
  setZoomLevel: (level: number) => void;
  // NETTOYÉ: Actions viewport centralisées
  setViewportScale: (scale: number) => void;
  setViewportPosition: (position: ViewportPosition) => void;
  setViewport: (viewport: Viewport) => void;
  setGridEnabled: (enabled: boolean) => void;
  setGridSize: (size: number) => void;
  setSnapToGrid: (enabled: boolean) => void;
  setAlignmentGuidesEnabled: (enabled: boolean) => void;
  setBackgroundColor: (color: string) => void;
  setTemplateDimensions: (width: number, height: number) => void;
  setPreviewMode: (enabled: boolean) => void;
  setExporting: (exporting: boolean) => void;
  setDocumentName: (name: string) => void;
  // Selected node properties actions;
  setSelectedNodeProperties: (properties: SelectedNodeProperties | null) => void;
  updateSelectedNodeProperty: (property: string, value: unknown) => void;
  updateSelectedNodeProperties: (properties: Partial<SelectedNodeProperties>) => void;
  // UI actions;
  setLeftSidebarCollapsed: (collapsed: boolean) => void;
  setRightSidebarCollapsed: (collapsed: boolean) => void;
  setActiveTool: (tool: string | null) => void;
  setShowColorPicker: (show: boolean) => void;
  setShowTemplateGallery: (show: boolean) => void;
  setShowAssetsLibrary: (show: boolean) => void;
  // History actions;
  setHistoryState: (canUndo: boolean, canRedo: boolean, length: number) => void;
  // Layer actions;
  setLayerCount: (count: number) => void;
  setLayerOrder: (order: string[]) => void;
  // TextTool actions;
  setActiveTextStyle: (styleId: string | null) => void;
  addTextStyle: (style: TextStyle) => void;
  updateTextStyle: (styleId: string, updates: Partial<TextStyle>) => void;
  deleteTextStyle: (styleId: string) => void;
  setShowTextStylesPanel: (show: boolean) => void;
  // Save actions;
  setSaveStatus: (status: SaveStatus, errorMessage?: string) => void;
  markAsSaved: () => void;
  markAsUnsaved: () => void;
  // Actions composites;
  resetCanvas: () => void;
  resetUI: () => void;
  resetTextTool: () => void;
  resetAll: () => void;

  // ============================================================================
  // PHASE 1: Actions Project
  // ============================================================================
  setProjectId: (id: string | null) => void;
  setProjectName: (name: string) => void;
  setProjectFormat: (format: PhysicalFormat) => void;
  setProjectDpi: (dpi: number) => void;
  setTargetBooth: (booth: TargetBoothType) => void;
  setPrintSettings: (settings: Partial<PrintSettings>) => void;
  setSafeZoneVisible: (visible: boolean) => void;
  setBleedVisible: (visible: boolean) => void;
  initializeProject: (config: { format: PhysicalFormat; dpi: number; name?: string }) => void;
  updateProjectDimensions: () => void;
  // PHASE 1: Méthodes de compatibilité avec ProjectStateService
  updateFormat: (format: TemplateFormat, dpi?: number) => void;
  resetProject: () => void;

  // ============================================================================
  // PHASE 1: Actions Selection (Multi-select robuste)
  // ============================================================================
  selectShape: (id: string, addToSelection?: boolean) => void;
  deselectShape: (id: string) => void;
  selectShapes: (ids: string[]) => void;
  clearSelection: () => void;
  selectAll: () => void;
  setSelectionMode: (mode: 'single' | 'multi' | 'marquee') => void;
  setMarqueeRect: (rect: { x: number; y: number; width: number; height: number } | null) => void;
  toggleShapeSelection: (id: string) => void;
  // Actions d'historique (injectées par le middleware)
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  commitHistory: () => void; // Sauvegarde explicite de l'historique (à appeler sur onDragEnd, onTransformEnd, delete)
  // Action pour changer de calque
  changeShapeLayer: (id: string, newLayer: 'background' | 'photos' | 'overlay') => void;

  // ============================================================================
  // PR-08: Actions Gesture (transaction unique d'historique)
  // ============================================================================
  beginGesture: (type: GestureType, ids?: string[]) => void;
  endGesture: () => void;

  // PR-16 : sélection d'un groupe entier à partir d'un clic Alt+Click sur une de ses shapes.
  selectGroupOfShape: (shapeId: string) => void;

  // PR-14 : actions sur la sélection multiple (groupes, flip, lock, hide).
  flipSelection: (axis: 'horizontal' | 'vertical', mode?: 'mirror' | 'flip') => void;
  groupSelection: () => void;
  ungroupSelection: () => void;
  toggleLockSelection: () => void;
  toggleHideSelection: () => void;

  // ============================================================================
  // PR-12: Actions Align / Distribute (multi-select)
  // ============================================================================
  alignSelection: (mode: AlignMode) => void;
  distributeSelection: (mode: DistributeMode) => void;

  // ============================================================================
  // PR-13: Actions Rotation / Scale (multi-select)
  // ============================================================================
  rotateSelection: (deltaDeg: number) => void;
  scaleSelection: (factor: number) => void;
}
// Store principal avec middleware d'historique
export const useAppStore = create<AppState & AppActions>()(
  devtools(
    // P3 : @ts-expect-error — la contrainte `StoreWithHistory` du middleware
    // n'est pas exactement satisfaite par AppState&AppActions mais la sémantique
    // est correcte (shapes + history slice). Cast plus propre = trop intrusif.
    // @ts-expect-error narrow constraint mismatch
    zustandHistoryMiddleware<AppState & AppActions>((set, get, _api) => ({
      // État initial - SHAPES (Source unique de vérité);
      shapes: [],

      // État initial;
      canvas: {
        // NETTOYÉ: selectedNodeId et selectedNodeIds supprimés - utiliser selection.selectedIds et selection.primaryId
        selectedNodeProperties: null,
        hoveredNodeId: null, // PHASE 3.1: État initial pour le hover
        zoomLevel: 1,
        // NETTOYÉ: Viewport centralisé (source unique de vérité)
        viewport: {
          scale: 1, // Synchronisé avec zoomLevel
          position: { x: 0, y: 0 }, // Position initiale du stage
        },
        gridEnabled: true,
        gridSize: 20,
        snapToGrid: false,
        alignmentGuidesEnabled: true,
        backgroundColor: '#ffffff',
        templateWidth: 800,
        templateHeight: 600,
        isPreviewMode: false,
        isExporting: false,
        documentName: 'Document sans titre',
      },
      ui: {
        leftSidebarCollapsed: false,
        rightSidebarCollapsed: false,
        activeTool: null,
        showColorPicker: false,
        showTemplateGallery: false,
        showAssetsLibrary: false,
      },
      history: {
        canUndo: false,
        canRedo: false,
        historyLength: 0,
      },
      layers: {
        layerCount: 0,
        layerOrder: [],
      },
      textTool: {
        activeTextStyle: null,
        savedStyles: [],
        showTextStylesPanel: false,
      },
      save: {
        status: 'idle',
        lastSaved: null,
      },

      // PHASE 1: État initial du projet
      project: {
        id: null,
        name: 'Nouveau projet',
        createdAt: null,
        modifiedAt: null,
        projectVersion: '1.0.0', // Version initiale du format
        format: PRESET_FORMATS[0], // 10x15cm par défaut
        dpi: PRESET_DPI.PRINT.value, // 300 DPI par défaut
        pixelWidth: Math.round((100 / 25.4) * 300), // ~1181 pixels
        pixelHeight: Math.round((150 / 25.4) * 300), // ~1772 pixels
        targetBooth: 'generic',
        printSettings: {
          bleedMm: 0.5, // Standard : 0.5mm pour le fond perdu
          safeZoneMm: 3, // Standard : 3mm pour la zone de sécurité
          cutMarkEnabled: false,
        },
        safeZoneVisible: true,
        bleedVisible: true, // Afficher le fond perdu par défaut
      },

      // PHASE 1: État initial de la sélection
      selection: {
        selectedIds: [],
        primaryId: null,
        mode: 'single',
        marqueeRect: null,
        lastClickedId: null,
      },

      // PR-08: État initial du geste
      gesture: {
        active: false,
        type: null,
        ids: [],
        startedAt: null,
      },

      // PR-16: État initial des groupes
      groups: [],

      // Actions Shapes (MIGRATION REACT: Source unique de vérité);
      addShape: shape => {
        // P1.4 FIX: Normaliser la shape avant l'ajout (garantit tous les champs nécessaires)
        // FIX runtime : import statique (require() lançait "require is not defined"
        // en ESM/Vite, et le catch silencieux SAUTAIT la normalisation à chaque ajout).
        let normalizedShape = shape;
        try {
          normalizedShape = normalizeShapeData(shape);
        } catch (_e) {
          // En cas d'échec de normalisation, utiliser la shape telle quelle.
        }

        // NETTOYÉ: Utiliser import() dynamique au lieu de require() (plus clean pour le navigateur)
        // Émettre l'événement d'historique de manière asynchrone
        import('./HistoryEventEmitter')
          .then(({ historyEventEmitter }) => {
            historyEventEmitter.emitHistoryAction({
              type: 'addShape',
              description: `Ajout ${normalizedShape.type}`,
              data: { action: 'addShape', shape: normalizedShape },
            });
          })
          .catch(() => {
            // HistoryEventEmitter peut ne pas être disponible, continuer quand même
          });

        // Émettre événement via EventBus pour découpler
        try {
          const eventBus = EventBus.getInstance();
          eventBus.emit<ShapeAddedEventData>({
            type: StateServiceEventType.SHAPE_ADDED,
            data: { shape: normalizedShape },
            timestamp: Date.now(),
          });
        } catch (_e) {
          // EventBus peut ne pas être disponible, continuer quand même
        }

        // PR-11: Sélectionner la nouvelle shape dans selection uniquement
        const newState = set(state => ({
          shapes: [...state.shapes, normalizedShape],
          selection: {
            ...state.selection,
            selectedIds: [normalizedShape.id],
            primaryId: normalizedShape.id,
            lastClickedId: normalizedShape.id,
          },
          save: { ...state.save, status: 'unsaved' },
        }));

        // Sauvegarder l'historique après l'ajout
        // NOTE: commitHistory() doit être appelé après chaque action utilisateur
        setTimeout(() => get().commitHistory(), 0);
        return newState;
      },

      updateShape: (id, updates) => {
        // Enregistrer dans l'historique (seulement pour les modifications importantes)
        const significantUpdates = ['x', 'y', 'width', 'height', 'rotation', 'text', 'fill'];
        const hasSignificantUpdate = Object.keys(updates).some(key =>
          significantUpdates.includes(key)
        );

        if (hasSignificantUpdate) {
          // NETTOYÉ: Utiliser import() dynamique au lieu de require()
          import('./HistoryEventEmitter')
            .then(({ historyEventEmitter }) => {
              historyEventEmitter.emitHistoryAction({
                type: 'updateShape',
                description: `Modification ${id}`,
                data: { action: 'updateShape', id, updates },
              });
            })
            .catch(() => {
              // HistoryEventEmitter peut ne pas être disponible, continuer quand même
            });
        }

        // Émettre événement via EventBus pour découpler
        try {
          const eventBus = EventBus.getInstance();
          eventBus.emit<ShapeUpdatedEventData>({
            type: StateServiceEventType.SHAPE_UPDATED,
            data: { shapeId: id, updates },
            timestamp: Date.now(),
          });
        } catch (_e) {
          // EventBus peut ne pas être disponible, continuer quand même
        }

        return set(state => ({
          shapes: state.shapes.map(shape => (shape.id === id ? { ...shape, ...updates } : shape)),
          save: { ...state.save, status: 'unsaved' },
        }));
      },

      // Action pour appliquer une transformation (transform/drag)
      // Utilisée par le Transformer pour mettre à jour position/size/rotation
      applyTransform: (id, transform) => {
        const significantUpdates = ['x', 'y', 'width', 'height', 'rotation'];
        const hasSignificantUpdate = Object.keys(transform).some(key =>
          significantUpdates.includes(key)
        );

        if (hasSignificantUpdate) {
          // NETTOYÉ: Utiliser import() dynamique au lieu de require()
          import('./HistoryEventEmitter')
            .then(({ historyEventEmitter }) => {
              historyEventEmitter.emitHistoryAction({
                // P3 : 'applyTransform' n'est pas dans l'union de base, on
                // assimile sémantiquement à updateShape pour l'historique.
                type: 'updateShape',
                description: `Transformation ${id}`,
                data: { action: 'applyTransform', id, transform },
              });
            })
            .catch(() => {
              // HistoryEventEmitter peut ne pas être disponible, continuer quand même
            });
        }

        // Émettre événement via EventBus pour découpler
        try {
          const eventBus = EventBus.getInstance();
          eventBus.emit<ShapeUpdatedEventData>({
            type: StateServiceEventType.SHAPE_UPDATED,
            data: { shapeId: id, updates: transform },
            timestamp: Date.now(),
          });
        } catch (_e) {
          // EventBus peut ne pas être disponible, continuer quand même
        }

        return set(state => ({
          shapes: state.shapes.map(shape =>
            shape.id === id
              ? {
                  ...shape,
                  x: transform.x,
                  y: transform.y,
                  width: transform.width,
                  height: transform.height,
                  rotation: transform.rotation,
                }
              : shape
          ),
          save: { ...state.save, status: 'unsaved' },
        }));
      },

      // PHASE 3: Batch updates pour éviter le spam d'historique
      // Permet de mettre à jour plusieurs shapes en une seule opération
      // Utile pour les opérations d'alignement qui modifient plusieurs éléments
      updateShapes: updates => {
        if (updates.length === 0) return;

        // Vérifier s'il y a des modifications significatives
        const significantUpdates = ['x', 'y', 'width', 'height', 'rotation', 'text', 'fill'];
        const hasSignificantUpdate = updates.some(({ updates: shapeUpdates }) =>
          Object.keys(shapeUpdates).some(key => significantUpdates.includes(key))
        );

        // Enregistrer dans l'historique UNE SEULE FOIS pour toutes les mises à jour
        if (hasSignificantUpdate) {
          // NETTOYÉ: Utiliser import() dynamique au lieu de require()
          import('./HistoryEventEmitter')
            .then(({ historyEventEmitter }) => {
              historyEventEmitter.emitHistoryAction({
                type: 'updateShapes',
                description: `Modification de ${updates.length} élément${updates.length > 1 ? 's' : ''}`,
                data: { action: 'updateShapes', updates },
              });
            })
            .catch(() => {
              // HistoryEventEmitter peut ne pas être disponible, continuer quand même
            });
        }

        // Créer un Map pour un accès rapide
        const updatesMap = new Map(updates.map(u => [u.id, u.updates]));

        const newState = set(state => ({
          shapes: state.shapes.map(shape => {
            const shapeUpdates = updatesMap.get(shape.id);
            return shapeUpdates ? { ...shape, ...shapeUpdates } : shape;
          }),
          save: { ...state.save, status: 'unsaved' },
        }));

        // Sauvegarder l'historique après les mises à jour batch
        // NOTE: commitHistory() doit être appelé après chaque action utilisateur
        // PR-10: Ne pas commit pendant une gesture (endGesture() gère l'unique commit)
        if (hasSignificantUpdate && !get().gesture?.active) {
          setTimeout(() => {
            get().commitHistory();
          }, 0);
        }

        return newState;
      },

      deleteShape: id => {
        // PHASE 2: Nettoyer les Blob URLs avant de supprimer la shape
        const shapeToDelete = get().shapes.find(s => s.id === id);
        if (shapeToDelete?.src && shapeToDelete.src.startsWith('blob:')) {
          // NETTOYÉ: Utiliser import() dynamique au lieu de require()
          import('./FileUploadService')
            .then(({ FileUploadService }) => {
              const fileUploadService = FileUploadService.getInstance();
              fileUploadService.revokeBlobURL(shapeToDelete.src as string);
            })
            .catch(() => {
              // FileUploadService peut ne pas être disponible, continuer quand même
            });
        }

        // Enregistrer dans l'historique
        // NETTOYÉ: Utiliser import() dynamique au lieu de require()
        import('./HistoryEventEmitter')
          .then(({ historyEventEmitter }) => {
            historyEventEmitter.emitHistoryAction({
              type: 'deleteShape',
              description: `Suppression ${id}`,
              data: { action: 'deleteShape', id },
            });
          })
          .catch(() => {
            // HistoryEventEmitter peut ne pas être disponible, continuer quand même
          });

        // Émettre événement via EventBus pour découpler
        try {
          const eventBus = EventBus.getInstance();
          eventBus.emit<ShapeDeletedEventData>({
            type: StateServiceEventType.SHAPE_DELETED,
            data: { shapeId: id },
            timestamp: Date.now(),
          });
        } catch (_e) {
          // EventBus peut ne pas être disponible, continuer quand même
        }

        // PR-11: Utiliser selection.selectedIds comme source unique
        const newState = set(state => {
          const nextSelectedIds = state.selection.selectedIds.filter(sid => sid !== id);
          const nextPrimary =
            state.selection.primaryId === id
              ? nextSelectedIds.length
                ? nextSelectedIds[nextSelectedIds.length - 1]
                : null
              : state.selection.primaryId;

          return {
            shapes: state.shapes.filter(shape => shape.id !== id),
            selection: {
              ...state.selection,
              selectedIds: nextSelectedIds,
              primaryId: nextPrimary,
            },
            save: { ...state.save, status: 'unsaved' },
          };
        });

        // Sauvegarder l'historique après la suppression
        // NOTE: commitHistory() doit être appelé après chaque action utilisateur
        setTimeout(() => {
          get().commitHistory();
        }, 0);

        return newState;
      },

      duplicateShape: id =>
        set(state => {
          const shapeToDuplicate = state.shapes.find(s => s.id === id);
          if (!shapeToDuplicate) return state;

          const newId = generateShapeId();
          const newShape: ShapeData = {
            ...shapeToDuplicate,
            id: newId,
            x: shapeToDuplicate.x + 20,
            y: shapeToDuplicate.y + 20,
          };

          // PR-11: Sélectionner la nouvelle shape dans selection uniquement
          return {
            shapes: [...state.shapes, newShape],
            selection: {
              ...state.selection,
              selectedIds: [newId],
              primaryId: newId,
              lastClickedId: newId,
            },
            save: { ...state.save, status: 'unsaved' },
          };
        }),

      loadShapes: rawShapes => {
        const sanitized = sanitizeShapes(rawShapes);
        set(() => ({
          shapes: sanitized,
        }));
      },

      clearShapes: () =>
        set(() => ({
          shapes: [],
          // NETTOYÉ: Utiliser selection.selectedIds au lieu de canvas.selectedNodeIds
          selection: {
            ...get().selection,
            selectedIds: [],
            primaryId: null,
          },
        })),

      moveShapeUp: id =>
        set(state => {
          const index = state.shapes.findIndex(s => s.id === id);
          if (index === -1 || index === state.shapes.length - 1) return state;

          const newShapes = [...state.shapes];
          [newShapes[index], newShapes[index + 1]] = [newShapes[index + 1], newShapes[index]];

          return { shapes: newShapes };
        }),

      moveShapeDown: id =>
        set(state => {
          const index = state.shapes.findIndex(s => s.id === id);
          if (index <= 0) return state;

          const newShapes = [...state.shapes];
          [newShapes[index], newShapes[index - 1]] = [newShapes[index - 1], newShapes[index]];

          return { shapes: newShapes };
        }),

      moveShapeToTop: id =>
        set(state => {
          const shape = state.shapes.find(s => s.id === id);
          if (!shape) return state;

          const newShapes = state.shapes.filter(s => s.id !== id);
          newShapes.push(shape);

          return { shapes: newShapes };
        }),

      moveShapeToBottom: id =>
        set(state => {
          const shape = state.shapes.find(s => s.id === id);
          if (!shape) return state;

          const newShapes = state.shapes.filter(s => s.id !== id);
          newShapes.unshift(shape);

          return { shapes: newShapes };
        }),

      // Alias pour cohérence avec le plan (Phase 2.1)
      bringToFront: id => get().moveShapeToTop(id),
      sendToBack: id => get().moveShapeToBottom(id),
      forward: id => get().moveShapeUp(id),
      backward: id => get().moveShapeDown(id),

      // Actions Group;
      groupShapes: shapeIds =>
        set(state => {
          if (shapeIds.length < 2) {
            // Pas besoin de logger ici, c'est une validation normale
            return state;
          }

          // Récupérer les formes à grouper
          const shapesToGroup = state.shapes.filter(s => shapeIds.includes(s.id));
          if (shapesToGroup.length < 2) {
            return state;
          }

          // Calculer les limites du groupe
          let minX = Infinity,
            minY = Infinity,
            maxX = -Infinity,
            maxY = -Infinity;
          shapesToGroup.forEach(shape => {
            const x = shape.x || 0;
            const y = shape.y || 0;
            const width = shape.width || 0;
            const height = shape.height || 0;
            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            maxX = Math.max(maxX, x + width);
            maxY = Math.max(maxY, y + height);
          });

          // Créer le groupe
          const groupId = generateShapeId();
          const group: ShapeData = {
            id: groupId,
            type: 'group',
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY,
            children: shapesToGroup.map(shape => ({
              ...shape,
              x: (shape.x || 0) - minX,
              y: (shape.y || 0) - minY,
            })),
            opacity: 1,
            visible: true,
            locked: false,
          };

          // Retirer les formes groupées et ajouter le groupe
          const remainingShapes = state.shapes.filter(s => !shapeIds.includes(s.id));
          // PR-11: Synchroniser la sélection du groupe avec selection uniquement
          return {
            shapes: [...remainingShapes, group],
            selection: {
              ...state.selection,
              selectedIds: [groupId],
              primaryId: groupId,
              lastClickedId: groupId,
            },
            save: { ...state.save, status: 'unsaved' },
          };
        }),

      ungroupShape: groupId =>
        set(state => {
          const group = state.shapes.find(s => s.id === groupId && s.type === 'group');
          if (!group || !group.children) {
            // Pas besoin de logger ici, c'est une validation normale
            return state;
          }

          // Récupérer les enfants avec leurs positions absolues
          const ungroupedShapes = group.children.map(child => ({
            ...child,
            x: (group.x || 0) + (child.x || 0),
            y: (group.y || 0) + (child.y || 0),
          }));

          // Retirer le groupe et ajouter les formes dégroupées
          const remainingShapes = state.shapes.filter(s => s.id !== groupId);
          const ungroupedIds = ungroupedShapes.map(s => s.id);
          const primaryId = ungroupedShapes[0]?.id || null;
          // PR-11: Synchroniser la sélection du dégroupage avec selection uniquement
          return {
            shapes: [...remainingShapes, ...ungroupedShapes],
            selection: {
              ...state.selection,
              selectedIds: ungroupedIds,
              primaryId,
              lastClickedId: primaryId,
            },
            save: { ...state.save, status: 'unsaved' },
          };
        }),

      // PR-11: Actions Canvas - alias vers selection (compatibilité legacy)
      setSelectedNode: nodeId =>
        set(state => {
          if (!nodeId) {
            return {
              selection: {
                ...state.selection,
                selectedIds: [],
                primaryId: null,
                marqueeRect: null,
              },
            };
          }
          return {
            selection: {
              ...state.selection,
              selectedIds: [nodeId],
              primaryId: nodeId,
              lastClickedId: nodeId,
            },
          };
        }),
      setSelectedNodes: nodeIds =>
        set(state => ({
          selection: {
            ...state.selection,
            selectedIds: nodeIds,
            primaryId: nodeIds.length ? nodeIds[nodeIds.length - 1] : null,
          },
        })),
      // PHASE 3.1: Action pour le feedback hover bidirectionnel
      setHoveredNode: nodeId =>
        set(state => ({
          canvas: { ...state.canvas, hoveredNodeId: nodeId },
        })),
      setZoomLevel: level =>
        set(state => {
          const previousZoomLevel = state.canvas.zoomLevel;

          // Émettre événement via EventBus pour découpler
          try {
            const eventBus = EventBus.getInstance();
            eventBus.emit<CanvasZoomChangedEventData>({
              type: StateServiceEventType.CANVAS_ZOOM_CHANGED,
              data: { zoomLevel: level, previousZoomLevel },
              timestamp: Date.now(),
            });
          } catch (_e) {
            // EventBus peut ne pas être disponible, continuer quand même
          }

          return {
            canvas: {
              ...state.canvas,
              zoomLevel: level,
              // NETTOYÉ: Synchroniser viewport.scale avec zoomLevel
              viewport: {
                ...state.canvas.viewport,
                scale: level,
              },
            },
          };
        }),

      // NETTOYÉ: Actions viewport centralisées (source unique de vérité)
      setViewportScale: scale =>
        set(state => ({
          canvas: {
            ...state.canvas,
            zoomLevel: scale, // Synchroniser avec zoomLevel
            viewport: {
              ...state.canvas.viewport,
              scale,
            },
          },
        })),

      setViewportPosition: position =>
        set(state => ({
          canvas: {
            ...state.canvas,
            viewport: {
              ...state.canvas.viewport,
              position,
            },
          },
        })),

      setViewport: viewport =>
        set(state => ({
          canvas: {
            ...state.canvas,
            zoomLevel: viewport.scale, // Synchroniser avec zoomLevel
            viewport,
          },
        })),
      setGridEnabled: enabled =>
        set(state => ({
          canvas: { ...state.canvas, gridEnabled: enabled },
        })),
      setGridSize: size =>
        set(state => ({
          canvas: { ...state.canvas, gridSize: size },
        })),
      setSnapToGrid: enabled =>
        set(state => ({
          canvas: { ...state.canvas, snapToGrid: enabled },
        })),
      setAlignmentGuidesEnabled: enabled =>
        set(state => ({
          canvas: { ...state.canvas, alignmentGuidesEnabled: enabled },
        })),
      setBackgroundColor: color =>
        set(state => ({
          canvas: { ...state.canvas, backgroundColor: color },
        })),
      setTemplateDimensions: (width, height) =>
        set(state => {
          const previousWidth = state.canvas.templateWidth;
          const previousHeight = state.canvas.templateHeight;

          // Émettre événement via EventBus pour découpler
          try {
            const eventBus = EventBus.getInstance();
            eventBus.emit<ProjectDimensionsChangedEventData>({
              type: StateServiceEventType.PROJECT_DIMENSIONS_CHANGED,
              data: { width, height, previousWidth, previousHeight },
              timestamp: Date.now(),
            });
          } catch (_e) {
            // EventBus peut ne pas être disponible, continuer quand même
          }

          return {
            canvas: {
              ...state.canvas,
              templateWidth: width,
              templateHeight: height,
            },
            // CORRIGÉ: Synchroniser aussi avec project.pixelWidth/pixelHeight pour maintenir la cohérence
            // Cela garantit que le format choisi et le canvas sont toujours synchronisés
            project: {
              ...state.project,
              pixelWidth: width,
              pixelHeight: height,
              modifiedAt: new Date(),
            },
          };
        }),
      setPreviewMode: enabled =>
        set(state => ({
          canvas: { ...state.canvas, isPreviewMode: enabled },
        })),
      setExporting: exporting =>
        set(state => ({
          canvas: { ...state.canvas, isExporting: exporting },
        })),
      setDocumentName: name =>
        set(state => ({
          canvas: { ...state.canvas, documentName: name },
        })),
      // Actions Selected Node Properties;
      setSelectedNodeProperties: properties =>
        set(state => ({
          canvas: { ...state.canvas, selectedNodeProperties: properties },
        })),
      updateSelectedNodeProperty: (property, value) =>
        set(state => {
          const currentProperties = state.canvas.selectedNodeProperties || {};
          return {
            canvas: {
              ...state.canvas,
              selectedNodeProperties: {
                ...currentProperties,
                [property]: value,
              },
            },
          };
        }),
      updateSelectedNodeProperties: properties =>
        set(state => {
          const currentProperties = state.canvas.selectedNodeProperties || {};
          return {
            canvas: {
              ...state.canvas,
              selectedNodeProperties: {
                ...currentProperties,
                ...properties,
              },
            },
          };
        }),
      // Actions UI;
      setLeftSidebarCollapsed: collapsed =>
        set(state => ({
          ui: { ...state.ui, leftSidebarCollapsed: collapsed },
        })),
      setRightSidebarCollapsed: collapsed =>
        set(state => ({
          ui: { ...state.ui, rightSidebarCollapsed: collapsed },
        })),
      setActiveTool: tool =>
        set(state => ({
          ui: { ...state.ui, activeTool: tool },
        })),
      setShowColorPicker: show =>
        set(state => ({
          ui: { ...state.ui, showColorPicker: show },
        })),
      setShowTemplateGallery: show =>
        set(state => ({
          ui: { ...state.ui, showTemplateGallery: show },
        })),
      setShowAssetsLibrary: show =>
        set(state => ({
          ui: { ...state.ui, showAssetsLibrary: show },
        })),
      // Actions History;
      setHistoryState: (canUndo, canRedo, length) =>
        set(_state => ({
          history: { canUndo, canRedo, historyLength: length },
        })),
      // Actions Layers;
      setLayerCount: count =>
        set(state => ({
          layers: { ...state.layers, layerCount: count },
        })),
      setLayerOrder: order =>
        set(state => ({
          layers: { ...state.layers, layerOrder: order },
        })),
      // Actions TextTool;
      setActiveTextStyle: styleId =>
        set(state => ({
          textTool: { ...state.textTool, activeTextStyle: styleId },
        })),
      addTextStyle: style =>
        set(state => ({
          textTool: {
            ...state.textTool,
            savedStyles: [...state.textTool.savedStyles, style],
          },
        })),
      updateTextStyle: (styleId, updates) =>
        set(state => ({
          textTool: {
            ...state.textTool,
            savedStyles: state.textTool.savedStyles.map(style =>
              style.id === styleId ? { ...style, ...updates } : style
            ),
          },
        })),
      deleteTextStyle: styleId =>
        set(state => ({
          textTool: {
            ...state.textTool,
            savedStyles: state.textTool.savedStyles.filter(style => style.id !== styleId),
          },
        })),
      setShowTextStylesPanel: show =>
        set(state => ({
          textTool: { ...state.textTool, showTextStylesPanel: show },
        })),
      // Actions Save;
      setSaveStatus: (status, errorMessage) =>
        set(state => ({
          save: {
            ...state.save,
            status,
            errorMessage,
            ...(status === 'saved' ? { lastSaved: new Date() } : {}),
          },
        })),
      markAsSaved: () =>
        set(() => ({
          save: {
            status: 'saved',
            lastSaved: new Date(),
          },
        })),
      markAsUnsaved: () =>
        set(state => ({
          save: {
            ...state.save,
            status: 'unsaved',
          },
        })),
      // Actions composites;
      resetCanvas: () =>
        set(_state => ({
          shapes: [],
          // NETTOYÉ: selectedNodeId et selectedNodeIds supprimés - utiliser selection.selectedIds
          canvas: {
            selectedNodeProperties: null,
            hoveredNodeId: null, // PHASE 3.1: Ajout de hoveredNodeId manquant
            zoomLevel: 1,
            viewport: {
              scale: 1,
              position: { x: 0, y: 0 },
            },
            gridEnabled: true,
            gridSize: 20,
            snapToGrid: false,
            alignmentGuidesEnabled: true,
            backgroundColor: '#ffffff',
            templateWidth: 800,
            templateHeight: 600,
            isPreviewMode: false,
            isExporting: false,
            documentName: 'Document sans titre',
          },
        })),
      resetUI: () =>
        set(_state => ({
          ui: {
            leftSidebarCollapsed: false,
            rightSidebarCollapsed: false,
            activeTool: null,
            showColorPicker: false,
            showTemplateGallery: false,
            showAssetsLibrary: false,
          },
        })),
      resetTextTool: () =>
        set(state => ({
          textTool: {
            activeTextStyle: null,
            savedStyles: state.textTool.savedStyles, // Garder les styles sauvegardés;
            showTextStylesPanel: false,
          },
        })),
      resetAll: () =>
        set(() => ({
          shapes: [],
          // NETTOYÉ: selectedNodeId et selectedNodeIds supprimés - utiliser selection.selectedIds
          canvas: {
            selectedNodeProperties: null,
            hoveredNodeId: null, // PHASE 3.1: Ajout de hoveredNodeId manquant
            zoomLevel: 1,
            viewport: {
              scale: 1,
              position: { x: 0, y: 0 },
            },
            gridEnabled: true,
            gridSize: 20,
            snapToGrid: false,
            alignmentGuidesEnabled: true,
            backgroundColor: '#ffffff',
            templateWidth: 800,
            templateHeight: 600,
            isPreviewMode: false,
            isExporting: false,
            documentName: 'Document sans titre',
          },
          ui: {
            leftSidebarCollapsed: false,
            rightSidebarCollapsed: false,
            activeTool: null,
            showColorPicker: false,
            showTemplateGallery: false,
            showAssetsLibrary: false,
          },
          history: {
            canUndo: false,
            canRedo: false,
            historyLength: 0,
          },
          layers: {
            layerCount: 0,
            layerOrder: [],
          },
          textTool: {
            activeTextStyle: null,
            savedStyles: [],
            showTextStylesPanel: false,
          },
          selection: {
            selectedIds: [],
            primaryId: null,
            mode: 'single',
            marqueeRect: null,
            lastClickedId: null,
          },
        })),

      // ============================================================================
      // PHASE 1: Implémentation Actions Project
      // ============================================================================

      setProjectId: id =>
        set(state => ({
          project: { ...state.project, id, modifiedAt: new Date() },
        })),

      setProjectName: name =>
        set(state => ({
          project: { ...state.project, name, modifiedAt: new Date() },
        })),

      setProjectFormat: format =>
        set(state => {
          // Calculer les nouvelles dimensions en pixels
          const pixelWidth = Math.round((format.widthMm / 25.4) * state.project.dpi);
          const pixelHeight = Math.round((format.heightMm / 25.4) * state.project.dpi);
          return {
            project: {
              ...state.project,
              format,
              pixelWidth,
              pixelHeight,
              modifiedAt: new Date(),
            },
            // Synchroniser aussi avec canvas.templateWidth/Height
            canvas: {
              ...state.canvas,
              templateWidth: pixelWidth,
              templateHeight: pixelHeight,
            },
          };
        }),

      setProjectDpi: dpi =>
        set(state => {
          // Recalculer les dimensions en pixels avec le nouveau DPI
          const pixelWidth = Math.round((state.project.format.widthMm / 25.4) * dpi);
          const pixelHeight = Math.round((state.project.format.heightMm / 25.4) * dpi);
          return {
            project: {
              ...state.project,
              dpi,
              pixelWidth,
              pixelHeight,
              modifiedAt: new Date(),
            },
            canvas: {
              ...state.canvas,
              templateWidth: pixelWidth,
              templateHeight: pixelHeight,
            },
          };
        }),

      setTargetBooth: booth =>
        set(state => ({
          project: { ...state.project, targetBooth: booth, modifiedAt: new Date() },
        })),

      setPrintSettings: settings =>
        set(state => ({
          project: {
            ...state.project,
            printSettings: { ...state.project.printSettings, ...settings },
            modifiedAt: new Date(),
          },
        })),

      setSafeZoneVisible: visible =>
        set(state => ({
          project: { ...state.project, safeZoneVisible: visible },
        })),

      setBleedVisible: visible =>
        set(state => ({
          project: { ...state.project, bleedVisible: visible },
        })),

      initializeProject: config =>
        set(_state => {
          const pixelWidth = Math.round((config.format.widthMm / 25.4) * config.dpi);
          const pixelHeight = Math.round((config.format.heightMm / 25.4) * config.dpi);
          const now = new Date();
          // CORRIGÉ: Générer un UUID valide pour Supabase
          const generateUUID = (): string => {
            if (typeof crypto !== 'undefined' && crypto.randomUUID) {
              return crypto.randomUUID();
            }
            // Fallback pour les navigateurs qui ne supportent pas crypto.randomUUID
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
              const r = (Math.random() * 16) | 0;
              const v = c === 'x' ? r : (r & 0x3) | 0x8;
              return v.toString(16);
            });
          };
          return {
            project: {
              id: generateUUID(),
              name: config.name || 'Nouveau projet',
              createdAt: now,
              modifiedAt: now,
              format: config.format,
              dpi: config.dpi,
              pixelWidth,
              pixelHeight,
              targetBooth: 'generic',
              printSettings: {
                bleedMm: 0.5,
                safeZoneMm: 3,
                cutMarkEnabled: false,
              },
              safeZoneVisible: true,
              bleedVisible: true,
            },
            canvas: {
              selectedNodeProperties: null,
              hoveredNodeId: null, // PHASE 3.1: Ajout de hoveredNodeId manquant
              zoomLevel: 1,
              viewport: {
                scale: 1,
                position: { x: 0, y: 0 },
              },
              gridEnabled: true,
              gridSize: 20,
              snapToGrid: false,
              alignmentGuidesEnabled: true,
              backgroundColor: '#ffffff',
              templateWidth: pixelWidth,
              templateHeight: pixelHeight,
              isPreviewMode: false,
              isExporting: false,
              documentName: config.name || 'Document sans titre',
            },
            shapes: [],
            selection: {
              selectedIds: [],
              primaryId: null,
              mode: 'single',
              marqueeRect: null,
              lastClickedId: null,
            },
          } as unknown as Partial<AppState & AppActions>;
        }),

      updateProjectDimensions: () =>
        set(state => {
          const pixelWidth = Math.round((state.project.format.widthMm / 25.4) * state.project.dpi);
          const pixelHeight = Math.round(
            (state.project.format.heightMm / 25.4) * state.project.dpi
          );
          return {
            project: { ...state.project, pixelWidth, pixelHeight },
            canvas: {
              ...state.canvas,
              templateWidth: pixelWidth,
              templateHeight: pixelHeight,
            },
          };
        }),

      // PHASE 1: Méthodes de compatibilité avec ProjectStateService
      // Met à jour le format depuis un TemplateFormat (avec unit)
      updateFormat: (format: TemplateFormat, dpi?: number) =>
        set(state => {
          // Convertir TemplateFormat vers PhysicalFormat
          const widthMm = convertToMm(format.width, format.unit, state.project.dpi);
          const heightMm = convertToMm(format.height, format.unit, state.project.dpi);
          const newDpi = dpi || state.project.dpi;

          const physicalFormat: PhysicalFormat = {
            id: format.name.toLowerCase().replace(/\s+/g, '_'),
            name: format.name,
            widthMm,
            heightMm,
            description: format.description,
          };

          // Émettre événement via EventBus pour découpler
          try {
            const eventBus = EventBus.getInstance();
            eventBus.emit<ProjectFormatChangedEventData>({
              type: StateServiceEventType.PROJECT_FORMAT_CHANGED,
              data: { format: physicalFormat, targetBoothType: state.project.targetBooth },
              timestamp: Date.now(),
            });
          } catch (_e) {
            // EventBus peut ne pas être disponible, continuer quand même
          }

          const pixelWidth = Math.round((widthMm / 25.4) * newDpi);
          const pixelHeight = Math.round((heightMm / 25.4) * newDpi);

          return {
            project: {
              ...state.project,
              format: physicalFormat,
              dpi: newDpi,
              pixelWidth,
              pixelHeight,
              modifiedAt: new Date(),
            },
            canvas: {
              ...state.canvas,
              templateWidth: pixelWidth,
              templateHeight: pixelHeight,
            },
          };
        }),

      // Réinitialise le projet (remplace ProjectStateService.reset())
      resetProject: () =>
        set(
          () =>
            ({
              project: {
                id: null,
                name: 'Nouveau projet',
                createdAt: null,
                modifiedAt: null,
                format: PRESET_FORMATS[0],
                dpi: PRESET_DPI.PRINT.value,
                pixelWidth: Math.round((100 / 25.4) * 300),
                pixelHeight: Math.round((150 / 25.4) * 300),
                targetBooth: 'generic',
                printSettings: {
                  bleedMm: 0.5,
                  safeZoneMm: 3,
                  cutMarkEnabled: false,
                },
                safeZoneVisible: true,
                bleedVisible: true,
              },
            }) as Partial<AppState & AppActions>
        ),

      // ============================================================================
      // PHASE 1: Implémentation Actions Selection (Multi-select robuste)
      // ============================================================================

      selectShape: (id, addToSelection = false) =>
        set(state => {
          const previousIds = [...state.selection.selectedIds];
          let newIds: string[];

          if (addToSelection) {
            // Multi-sélection (Ctrl/Cmd + Click)
            newIds = state.selection.selectedIds.includes(id)
              ? state.selection.selectedIds
              : [...state.selection.selectedIds, id];
          } else {
            // Sélection simple
            newIds = [id];
          }

          // Émettre événement via EventBus pour découpler
          try {
            const eventBus = EventBus.getInstance();
            eventBus.emit<SelectionChangedEventData>({
              type: StateServiceEventType.SELECTION_CHANGED,
              data: { selectedIds: newIds, previousIds },
              timestamp: Date.now(),
            });
          } catch (_e) {
            // EventBus peut ne pas être disponible, continuer quand même
          }

          return {
            selection: {
              ...state.selection,
              selectedIds: newIds,
              primaryId: id,
              lastClickedId: id,
            },
          };
        }),

      deselectShape: id =>
        set(state => {
          const newIds = state.selection.selectedIds.filter(sid => sid !== id);
          const newPrimaryId = newIds.length > 0 ? newIds[newIds.length - 1] : null;
          // PR-11: Utiliser selection uniquement
          return {
            selection: {
              ...state.selection,
              selectedIds: newIds,
              primaryId: newPrimaryId,
            },
          };
        }),

      selectShapes: ids =>
        set(state => {
          const primary = ids.length > 0 ? ids[ids.length - 1] : null;
          // PR-11: Utiliser selection uniquement
          return {
            selection: {
              ...state.selection,
              selectedIds: ids,
              primaryId: primary,
              lastClickedId: primary,
            },
          };
        }),

      clearSelection: () =>
        set(state => {
          const previousIds = [...state.selection.selectedIds];

          // Émettre événement via EventBus pour découpler
          try {
            const eventBus = EventBus.getInstance();
            eventBus.emit<SelectionChangedEventData>({
              type: StateServiceEventType.SELECTION_CHANGED,
              data: { selectedIds: [], previousIds },
              timestamp: Date.now(),
            });
          } catch (_e) {
            // EventBus peut ne pas être disponible, continuer quand même
          }

          // PR-11: Utiliser selection uniquement
          return {
            selection: {
              ...state.selection,
              selectedIds: [],
              primaryId: null,
              marqueeRect: null,
            },
          };
        }),

      selectAll: () =>
        set(state => {
          const allIds = state.shapes.map(s => s.id);
          const primary = allIds.length > 0 ? allIds[allIds.length - 1] : null;
          // PR-11: Utiliser selection uniquement
          return {
            selection: {
              ...state.selection,
              selectedIds: allIds,
              primaryId: primary,
              lastClickedId: primary,
            },
          };
        }),

      setSelectionMode: mode =>
        set(state => ({
          selection: { ...state.selection, mode },
        })),

      setMarqueeRect: rect =>
        set(state => ({
          selection: { ...state.selection, marqueeRect: rect },
        })),

      toggleShapeSelection: id =>
        set(state => {
          const isSelected = state.selection.selectedIds.includes(id);
          const newIds = isSelected
            ? state.selection.selectedIds.filter(sid => sid !== id)
            : [...state.selection.selectedIds, id];
          const newPrimaryId =
            newIds.length > 0 ? (isSelected ? newIds[newIds.length - 1] : id) : null;
          // PR-11: Utiliser selection uniquement
          return {
            selection: {
              ...state.selection,
              selectedIds: newIds,
              primaryId: newPrimaryId,
              lastClickedId: id,
            },
          };
        }),

      // Actions pour changer de calque (nouveau)
      changeShapeLayer: (id: string, newLayer: 'background' | 'photos' | 'overlay') =>
        set(state => ({
          shapes: state.shapes.map(shape =>
            shape.id === id ? { ...shape, layer: newLayer } : shape
          ),
          save: { ...state.save, status: 'unsaved' },
        })),

      // ============================================================================
      // PR-08: Implémentation Actions Gesture (transaction unique d'historique)
      // ============================================================================
      beginGesture: (type, ids = []) => {
        set(state => {
          if (state.gesture.active) {
            // Déjà en gesture → merge ids (utile pour multi-select)
            const merged = Array.from(new Set([...state.gesture.ids, ...ids]));
            return { gesture: { ...state.gesture, ids: merged } };
          }
          return {
            gesture: {
              active: true,
              type,
              ids,
              startedAt: Date.now(),
            },
          };
        });
      },

      endGesture: () => {
        const g = get().gesture;
        if (!g.active) return;

        set({
          gesture: {
            active: false,
            type: null,
            ids: [],
            startedAt: null,
          },
        });

        // ✅ UN SEUL commit à la fin du geste
        get().commitHistory();
      },

      // ============================================================================
      // PR-12: Implémentation Align / Distribute (multi-select)
      // ============================================================================
      alignSelection: mode => {
        const store = get();
        const ids = store.selection.selectedIds;
        if (!ids || ids.length === 0) return;

        const bbox = getSelectionBBox(store.shapes, ids);
        if (!bbox) return;

        store.beginGesture?.('property', ids);

        const updates = store.shapes
          .filter(s => ids.includes(s.id))
          .map(s => {
            const r = getShapeRect(s);

            let x = r.x;
            let y = r.y;

            if (mode === 'left') x = bbox.x;
            if (mode === 'right') x = bbox.right - r.width;
            if (mode === 'centerX') x = bbox.cx - r.width / 2;

            if (mode === 'top') y = bbox.y;
            if (mode === 'bottom') y = bbox.bottom - r.height;
            if (mode === 'centerY') y = bbox.cy - r.height / 2;

            return { id: s.id, updates: { x, y } };
          });

        store.updateShapes(updates);
        store.endGesture?.();
      },

      distributeSelection: mode => {
        const store = get();
        const ids = store.selection.selectedIds;
        if (!ids || ids.length < 3) return; // distribuer utile à partir de 3

        const selected = store.shapes.filter(s => ids.includes(s.id));
        if (selected.length < 3) return;

        const bbox = getSelectionBBox(store.shapes, ids);
        if (!bbox) return;

        store.beginGesture?.('property', ids);

        if (mode === 'horizontal') {
          // Tri par x (bord gauche)
          const sorted = [...selected].sort((a, b) => (a.x ?? 0) - (b.x ?? 0));

          const rects = sorted.map(s => ({ s, r: getShapeRect(s) }));

          const totalWidth = rects.reduce((acc, it) => acc + it.r.width, 0);
          const span = bbox.width;
          const gap = (span - totalWidth) / (rects.length - 1);

          // Si span trop petit → gap négatif (overlap). On accepte, ça "tasse".
          let cursorX = bbox.x;

          const updates = rects.map((it, _idx) => {
            const nextX = cursorX;
            cursorX = cursorX + it.r.width + gap;

            // on garde le y tel quel
            return { id: it.s.id, updates: { x: nextX, y: it.r.y } };
          });

          store.updateShapes(updates);
          store.endGesture?.();
          return;
        }

        if (mode === 'vertical') {
          const sorted = [...selected].sort((a, b) => (a.y ?? 0) - (b.y ?? 0));
          const rects = sorted.map(s => ({ s, r: getShapeRect(s) }));

          const totalHeight = rects.reduce((acc, it) => acc + it.r.height, 0);
          const span = bbox.height;
          const gap = (span - totalHeight) / (rects.length - 1);

          let cursorY = bbox.y;

          const updates = rects.map(it => {
            const nextY = cursorY;
            cursorY = cursorY + it.r.height + gap;

            return { id: it.s.id, updates: { x: it.r.x, y: nextY } };
          });

          store.updateShapes(updates);
          store.endGesture?.();
          return;
        }
      },

      // ============================================================================
      // PR-13: Implémentation Rotation / Scale (multi-select)
      // ============================================================================
      rotateSelection: deltaDeg => {
        const store = get();
        const ids = store.selection.selectedIds;
        if (!ids || ids.length === 0) return;

        const bbox = getSelectionBBox(store.shapes, ids);
        if (!bbox) return;

        store.beginGesture?.('property', ids);

        const updates = store.shapes
          .filter(s => ids.includes(s.id))
          .map(s => {
            const { width, height } = getShapeWHForOps(s);

            const currentCenter = getShapeWorldCenter(s);
            const nextCenter = rotatePointAround(
              currentCenter.x,
              currentCenter.y,
              bbox.cx,
              bbox.cy,
              deltaDeg
            );

            const nextRotation = (s.rotation ?? 0) + deltaDeg;

            // origin recalculé pour garder le centre monde correct malgré rotation top-left
            const nextOrigin = originFromWorldCenter(
              nextCenter.x,
              nextCenter.y,
              width,
              height,
              nextRotation
            );

            return {
              id: s.id,
              updates: {
                x: nextOrigin.x,
                y: nextOrigin.y,
                rotation: nextRotation,
              },
            };
          });

        store.updateShapes(updates);
        store.endGesture?.();
      },

      scaleSelection: factor => {
        const store = get();
        const ids = store.selection.selectedIds;
        if (!ids || ids.length === 0) return;

        const bbox = getSelectionBBox(store.shapes, ids);
        if (!bbox) return;

        // clamp (évite facteur absurde)
        const f = clamp(factor, 0.05, 20);

        store.beginGesture?.('property', ids);

        const updates = store.shapes
          .filter(s => ids.includes(s.id))
          .map(s => {
            const rot = s.rotation ?? 0;
            const { width, height } = getShapeWHForOps(s);

            const currentCenter = getShapeWorldCenter(s);

            // centre scale autour bbox center
            const nextCenter = {
              x: bbox.cx + (currentCenter.x - bbox.cx) * f,
              y: bbox.cy + (currentCenter.y - bbox.cy) * f,
            };

            // tailles scale
            const nextW = Math.max(5, width * f);
            const nextH = s.type === 'arrow' ? 0 : Math.max(5, height * f);

            // origin recalculé pour garder le centre monde cohérent
            const nextOrigin = originFromWorldCenter(nextCenter.x, nextCenter.y, nextW, nextH, rot);

            // styles (petit + réaliste)
            const styleScale = f;
            const nextStrokeWidth =
              s.strokeWidth != null ? Math.max(0.5, s.strokeWidth * styleScale) : undefined;
            const nextCornerRadius =
              s.cornerRadius != null ? Math.max(0, s.cornerRadius * styleScale) : undefined;

            const nextShadowBlur =
              s.shadowBlur != null ? Math.max(0, s.shadowBlur * styleScale) : undefined;
            const nextShadowOffsetX =
              s.shadowOffsetX != null ? s.shadowOffsetX * styleScale : undefined;
            const nextShadowOffsetY =
              s.shadowOffsetY != null ? s.shadowOffsetY * styleScale : undefined;

            const nextFontSize =
              s.type === 'text' && s.fontSize != null
                ? Math.max(6, s.fontSize * styleScale)
                : undefined;

            return {
              id: s.id,
              updates: {
                x: nextOrigin.x,
                y: nextOrigin.y,
                width: s.type === 'arrow' ? nextW : nextW,
                height: s.type === 'arrow' ? undefined : nextH,

                // styles
                strokeWidth: nextStrokeWidth,
                cornerRadius: nextCornerRadius,
                shadowBlur: nextShadowBlur,
                shadowOffsetX: nextShadowOffsetX,
                shadowOffsetY: nextShadowOffsetY,
                fontSize: nextFontSize,

                // (Optionnel) padding background texte si tu l'utilises
                textBackgroundPadding:
                  s.type === 'text' && s.textBackgroundPadding != null
                    ? Math.max(0, s.textBackgroundPadding * styleScale)
                    : undefined,
                textBackgroundCornerRadius:
                  s.type === 'text' && s.textBackgroundCornerRadius != null
                    ? Math.max(0, s.textBackgroundCornerRadius * styleScale)
                    : undefined,
              },
            };
          });

        store.updateShapes(updates);
        store.endGesture?.();
      },

      // ============================================================================
      // PR-14: Implémentation Mirror / Flip (multi-select)
      // ============================================================================
      flipSelection: (axis, mode = 'mirror') => {
        const store = get();
        const ids = store.selection.selectedIds;
        if (!ids || ids.length === 0) return;

        const bbox = getSelectionBBox(store.shapes, ids);
        if (!bbox) return;

        store.beginGesture?.('property', ids);

        const updates = store.shapes
          .filter(s => ids.includes(s.id))
          .map(s => {
            const r = getShapeRect(s);

            // Mirror layout
            let x = r.x;
            let y = r.y;

            if (axis === 'horizontal') {
              // miroir gauche/droite autour bbox.cx
              x = 2 * bbox.cx - (r.x + r.width);
            } else {
              // miroir haut/bas autour bbox.cy
              y = 2 * bbox.cy - (r.y + r.height);
            }

            // Flip orientation (option)
            let rotation = s.rotation ?? 0;
            if (mode === 'flip') {
              if (axis === 'horizontal') {
                // réflexion par rapport à une verticale : θ' = 180 - θ
                rotation = 180 - rotation;
              } else {
                // réflexion par rapport à une horizontale : θ' = -θ
                rotation = -rotation;
              }
            }

            return { id: s.id, updates: { x, y, rotation } };
          });

        store.updateShapes(updates);
        store.endGesture?.();
      },

      // ============================================================================
      // PR-16: Implémentation Grouping
      // ============================================================================
      groupSelection: () => {
        const store = get();
        const ids = store.selection.selectedIds;
        if (!ids || ids.length < 2) return;

        const generateGroupId = () => `group_${Date.now()}_${Math.random().toString(16).slice(2)}`;
        const groupId = generateGroupId();
        const group: GroupData = {
          id: groupId,
          name: `Group ${store.groups.length + 1}`,
          childIds: [...ids],
          createdAt: Date.now(),
        };

        store.beginGesture?.('property', ids);

        set(state => ({
          groups: [...state.groups, group],
          shapes: state.shapes.map(s => (ids.includes(s.id) ? { ...s, groupId } : s)),
          save: { ...state.save, status: 'unsaved' },
        }));

        store.endGesture?.();
      },

      ungroupSelection: () => {
        const store = get();
        const ids = store.selection.selectedIds;
        if (!ids || ids.length === 0) return;

        const touchedGroupIds = new Set(
          store.shapes.filter(s => ids.includes(s.id) && s.groupId).map(s => s.groupId as string)
        );

        if (touchedGroupIds.size === 0) return;

        store.beginGesture?.('property', ids);

        set(state => ({
          groups: state.groups.filter(g => !touchedGroupIds.has(g.id)),
          shapes: state.shapes.map(s =>
            s.groupId && touchedGroupIds.has(s.groupId) ? { ...s, groupId: null } : s
          ),
          save: { ...state.save, status: 'unsaved' },
        }));

        store.endGesture?.();
      },

      selectGroupOfShape: shapeId => {
        const store = get();
        const shape = store.shapes.find(s => s.id === shapeId);
        const groupId = shape?.groupId;
        if (!groupId) return;

        const groupIds = store.shapes.filter(s => s.groupId === groupId).map(s => s.id);

        set(state => ({
          selection: {
            ...state.selection,
            selectedIds: groupIds,
            primaryId: shapeId, // garde le clic comme primary
            lastClickedId: shapeId,
          },
        }));
      },

      // ============================================================================
      // PR-17: Implémentation Locked / Hidden
      // ============================================================================
      setShapeLocked: (id: string, locked: boolean) => {
        const store = get();
        store.beginGesture?.('property', [id]);

        set(state => ({
          shapes: state.shapes.map(s => (s.id === id ? { ...s, locked } : s)),
          save: { ...state.save, status: 'unsaved' },
        }));

        store.endGesture?.();
      },

      setShapeHidden: (id: string, hidden: boolean) => {
        const store = get();
        store.beginGesture?.('property', [id]);

        set(state => {
          const nextShapes = state.shapes.map(s => (s.id === id ? { ...s, hidden } : s));

          // ✅ Si on masque un objet sélectionné → il doit sortir de la sélection
          const nextSelectedIds = hidden
            ? state.selection.selectedIds.filter(sid => sid !== id)
            : state.selection.selectedIds;

          const nextPrimary =
            state.selection.primaryId === id && hidden
              ? nextSelectedIds.length
                ? nextSelectedIds[nextSelectedIds.length - 1]
                : null
              : state.selection.primaryId;

          return {
            shapes: nextShapes,
            selection: {
              ...state.selection,
              selectedIds: nextSelectedIds,
              primaryId: nextPrimary,
            },
            save: { ...state.save, status: 'unsaved' },
          };
        });

        store.endGesture?.();
      },

      toggleLockSelection: () => {
        const store = get();
        const ids = store.selection.selectedIds;
        if (!ids || ids.length === 0) return;

        const selected = store.shapes.filter(s => ids.includes(s.id));
        if (!selected.length) return;

        // règle simple : si au moins 1 non-lock => lock all, sinon unlock all
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const isShapeLocked = (s: any) => !!s.locked;
        const shouldLock = selected.some(s => !isShapeLocked(s));

        store.beginGesture?.('property', ids);

        set(state => ({
          shapes: state.shapes.map(s => (ids.includes(s.id) ? { ...s, locked: shouldLock } : s)),
          save: { ...state.save, status: 'unsaved' },
        }));

        store.endGesture?.();
      },

      toggleHideSelection: () => {
        const store = get();
        const ids = store.selection.selectedIds;
        if (!ids || ids.length === 0) return;

        const selected = store.shapes.filter(s => ids.includes(s.id));
        if (!selected.length) return;

        // règle simple : si au moins 1 visible => hide all, sinon show all
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const isShapeHidden = (s: any) => !!s.hidden;
        const shouldHide = selected.some(s => !isShapeHidden(s));

        store.beginGesture?.('property', ids);

        set(state => {
          const nextShapes = state.shapes.map(s =>
            ids.includes(s.id) ? { ...s, hidden: shouldHide } : s
          );

          // ✅ si on cache => on retire tous ces ids de la sélection
          const nextSelectedIds = shouldHide
            ? state.selection.selectedIds.filter(id => !ids.includes(id))
            : state.selection.selectedIds;

          const nextPrimary = shouldHide
            ? nextSelectedIds.length
              ? nextSelectedIds[nextSelectedIds.length - 1]
              : null
            : state.selection.primaryId;

          return {
            shapes: nextShapes,
            selection: {
              ...state.selection,
              selectedIds: nextSelectedIds,
              primaryId: nextPrimary,
            },
            save: { ...state.save, status: 'unsaved' },
          };
        });

        store.endGesture?.();
      },
    })),
    {
      name: 'snapbooth-app-store',
    }
  )
);
// Sélecteurs pour accéder facilement à l'état;
export const useCanvasState = () => useAppStore(state => state.canvas);
export const useUIState = () => useAppStore(state => state.ui);
export const useHistoryState = () => useAppStore(state => state.history);
export const useLayersState = () => useAppStore(state => state.layers);
export const useTextToolState = () => useAppStore(state => state.textTool);
// Sélecteurs spécifiques;
// NETTOYÉ: Utiliser selection.primaryId au lieu de canvas.selectedNodeId
export const useSelectedNode = () => useAppStore(state => state.selection.primaryId);
export const useZoomLevel = () => useAppStore(state => state.canvas.zoomLevel);
export const useGridEnabled = () => useAppStore(state => state.canvas.gridEnabled);
export const usePreviewMode = () => useAppStore(state => state.canvas.isPreviewMode);
// NETTOYÉ: Hooks viewport centralisés
export const useViewport = () => useAppStore(state => state.canvas.viewport);
export const useViewportScale = () => useAppStore(state => state.canvas.viewport.scale);
export const useViewportPosition = () => useAppStore(state => state.canvas.viewport.position);
// Actions spécifiques;
export const useCanvasActions = () =>
  useAppStore(state => ({
    setSelectedNode: state.setSelectedNode,
    setZoomLevel: state.setZoomLevel,
    setViewportScale: state.setViewportScale,
    setViewportPosition: state.setViewportPosition,
    setViewport: state.setViewport,
    setGridEnabled: state.setGridEnabled,
    setBackgroundColor: state.setBackgroundColor,
    setTemplateDimensions: state.setTemplateDimensions,
    setPreviewMode: state.setPreviewMode,
  }));
export const useUIActions = () =>
  useAppStore(state => ({
    setLeftSidebarCollapsed: state.setLeftSidebarCollapsed,
    setRightSidebarCollapsed: state.setRightSidebarCollapsed,
    setActiveTool: state.setActiveTool,
    setShowColorPicker: state.setShowColorPicker,
    setShowTemplateGallery: state.setShowTemplateGallery,
    setShowAssetsLibrary: state.setShowAssetsLibrary,
  }));
// Actions TextTool;
export const useTextToolActions = () =>
  useAppStore(state => ({
    setActiveTextStyle: state.setActiveTextStyle,
    addTextStyle: state.addTextStyle,
    updateTextStyle: state.updateTextStyle,
    deleteTextStyle: state.deleteTextStyle,
    setShowTextStylesPanel: state.setShowTextStylesPanel,
    resetTextTool: state.resetTextTool,
  }));
// Sélecteurs spécifiques TextTool;
export const useActiveTextStyle = () => useAppStore(state => state.textTool.activeTextStyle);
export const useSavedTextStyles = () => useAppStore(state => state.textTool.savedStyles);

// Sélecteurs Shapes (MIGRATION REACT);
export const useShapes = () => useAppStore(state => state.shapes);
export const useSelectedShape = () =>
  useAppStore(state => {
    const { shapes, selection } = state;
    // PR-11: Utiliser selection.primaryId
    return shapes.find(s => s.id === selection.primaryId) || null;
  });

// Actions Shapes;
export const useShapeActions = () =>
  useAppStore(state => ({
    addShape: state.addShape,
    updateShape: state.updateShape,
    deleteShape: state.deleteShape,
    duplicateShape: state.duplicateShape,
    loadShapes: state.loadShapes,
    clearShapes: state.clearShapes,
    moveShapeUp: state.moveShapeUp,
    moveShapeDown: state.moveShapeDown,
    moveShapeToTop: state.moveShapeToTop,
    moveShapeToBottom: state.moveShapeToBottom,
  }));

// ============================================================================
// PHASE 1: Sélecteurs et Hooks pour Project
// ============================================================================

export const useProjectState = () => useAppStore(state => state.project);
export const useProjectFormat = () => useAppStore(state => state.project.format);
export const useProjectDpi = () => useAppStore(state => state.project.dpi);
export const useProjectDimensions = () =>
  useAppStore(state => ({
    widthMm: state.project.format.widthMm,
    heightMm: state.project.format.heightMm,
    widthPx: state.project.pixelWidth,
    heightPx: state.project.pixelHeight,
    dpi: state.project.dpi,
  }));
export const usePrintSettings = () => useAppStore(state => state.project.printSettings);
export const useSafeZoneVisible = () => useAppStore(state => state.project.safeZoneVisible);
export const useBleedVisible = () => useAppStore(state => state.project.bleedVisible);

export const useProjectActions = () =>
  useAppStore(state => ({
    setProjectId: state.setProjectId,
    setProjectName: state.setProjectName,
    setProjectFormat: state.setProjectFormat,
    setProjectDpi: state.setProjectDpi,
    setTargetBooth: state.setTargetBooth,
    setPrintSettings: state.setPrintSettings,
    setSafeZoneVisible: state.setSafeZoneVisible,
    setBleedVisible: state.setBleedVisible,
    initializeProject: state.initializeProject,
    updateProjectDimensions: state.updateProjectDimensions,
    updateFormat: state.updateFormat,
    resetProject: state.resetProject,
  }));

// ============================================================================
// PHASE 1: Sélecteurs et Hooks pour Selection (Multi-select)
// ============================================================================

export const useSelectionState = () => useAppStore(state => state.selection);
export const useSelectedIds = () => useAppStore(state => state.selection.selectedIds);
export const usePrimarySelectedId = () => useAppStore(state => state.selection.primaryId);
export const useSelectionMode = () => useAppStore(state => state.selection.mode);
export const useMarqueeRect = () => useAppStore(state => state.selection.marqueeRect);

// Sélecteur pour obtenir les shapes sélectionnées
export const useSelectedShapes = () =>
  useAppStore(state => {
    const { selectedIds } = state.selection;
    return state.shapes.filter(s => selectedIds.includes(s.id));
  });

// Sélecteur pour obtenir la shape primaire sélectionnée
export const usePrimarySelectedShape = () =>
  useAppStore(state => {
    const { primaryId } = state.selection;
    if (!primaryId) return null;
    return state.shapes.find(s => s.id === primaryId) || null;
  });

// Vérifier si une shape est sélectionnée
export const useIsShapeSelected = (id: string) =>
  useAppStore(state => state.selection.selectedIds.includes(id));

// Compter les éléments sélectionnés
export const useSelectionCount = () => useAppStore(state => state.selection.selectedIds.length);

export const useSelectionActions = () =>
  useAppStore(state => ({
    selectShape: state.selectShape,
    deselectShape: state.deselectShape,
    selectShapes: state.selectShapes,
    clearSelection: state.clearSelection,
    selectAll: state.selectAll,
    setSelectionMode: state.setSelectionMode,
    setMarqueeRect: state.setMarqueeRect,
    toggleShapeSelection: state.toggleShapeSelection,
  }));

// ============================================================================
// Utilitaires pour calcul DPI/Dimensions
// ============================================================================

/**
 * Convertit des mm en pixels pour un DPI donné
 */
export function mmToPixels(mm: number, dpi: number): number {
  return Math.round((mm / 25.4) * dpi);
}

/**
 * Convertit des pixels en mm pour un DPI donné
 */
export function pixelsToMm(pixels: number, dpi: number): number {
  return (pixels * 25.4) / dpi;
}

/**
 * Calcule les dimensions de la zone de sécurité en pixels
 */
export function getSafeZonePixels(safeZoneMm: number, dpi: number): number {
  return mmToPixels(safeZoneMm, dpi);
}

/**
 * Calcule les dimensions du fond perdu en pixels
 */
export function getBleedPixels(bleedMm: number, dpi: number): number {
  return mmToPixels(bleedMm, dpi);
}

// ============================================================================
// PHASE 1: Selectors pour ProjectState (remplace ProjectStateService)
// ============================================================================

/**
 * Obtient les bounds de la zone de sécurité (safe zone) en pixels
 * Remplace ProjectStateService.getSafeZoneBounds()
 */
export function useSafeZoneBounds() {
  return useAppStore(state => {
    const { pixelWidth, pixelHeight, printSettings, dpi } = state.project;
    const safeZonePx = mmToPixels(printSettings.safeZoneMm, dpi);
    return {
      x: safeZonePx,
      y: safeZonePx,
      width: pixelWidth - safeZonePx * 2,
      height: pixelHeight - safeZonePx * 2,
    };
  });
}

/**
 * Obtient les bounds du fond perdu (bleed) en pixels
 * Remplace ProjectStateService.getBleedBounds()
 */
export function useBleedBounds() {
  return useAppStore(state => {
    const { pixelWidth, pixelHeight, printSettings, dpi } = state.project;
    const bleedPx = mmToPixels(printSettings.bleedMm, dpi);
    return {
      x: -bleedPx,
      y: -bleedPx,
      width: pixelWidth + bleedPx * 2,
      height: pixelHeight + bleedPx * 2,
    };
  });
}

/**
 * Helper pour obtenir les bounds de la zone de sécurité (version non-hook pour usage dans classes)
 */
export function getSafeZoneBoundsFromState(state: { project: ProjectState }): {
  x: number;
  y: number;
  width: number;
  height: number;
} | null {
  if (!state.project) return null;
  const { pixelWidth, pixelHeight, printSettings, dpi } = state.project;
  const safeZonePx = mmToPixels(printSettings.safeZoneMm, dpi);
  return {
    x: safeZonePx,
    y: safeZonePx,
    width: pixelWidth - safeZonePx * 2,
    height: pixelHeight - safeZonePx * 2,
  };
}

/**
 * Helper pour obtenir les bounds du fond perdu (version non-hook pour usage dans classes)
 */
export function getBleedBoundsFromState(state: { project: ProjectState }): {
  x: number;
  y: number;
  width: number;
  height: number;
} | null {
  if (!state.project) return null;
  const { pixelWidth, pixelHeight, printSettings, dpi } = state.project;
  const bleedPx = mmToPixels(printSettings.bleedMm, dpi);
  return {
    x: -bleedPx,
    y: -bleedPx,
    width: pixelWidth + bleedPx * 2,
    height: pixelHeight + bleedPx * 2,
  };
}

/**
 * Helper pour convertir une valeur d'une unité vers mm
 */
function convertToMm(value: number, unit: TemplateUnit, dpi: number): number {
  switch (unit) {
    case 'mm':
      return value;
    case 'cm':
      return value * 10;
    case 'inch':
      return value * 25.4;
    case 'px':
      return pixelsToMm(value, dpi);
    default:
      return value;
  }
}
